import React, { useCallback, useState } from 'react';
import { Loader2, Sparkles, RefreshCw, X, ImageOff, Maximize2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import Tooltip from '../ui/Tooltip';
import ReferenceUpload from './ReferenceUpload';
import { useStorageUrl } from '../chatHome/MemberAvatar';
import ImageLightbox from '../ui/ImageLightbox';
import { generateReferencePlate, type PlateKind } from '../../services/studioService';
import { useOptionalToast } from '../../contexts/NotificationContext';

/**
 * A REFERENCE PLATE — the picture of a place or an object, generated or uploaded, and SEEN before it matters.
 *
 * Owner 2026-08-30: *"after prompt, I think we should have a button to generate the reference images — this
 * lets user confirm or assure the isolated generation is what they wanted."*
 *
 * That is the whole argument for generating it here rather than at render time. A location described only in
 * prose is a guess that first becomes visible inside a finished shot, mixed with a character, a camera move
 * and a lighting choice — at which point a wrong-looking set is expensive to notice and hard to attribute. Made
 * in ISOLATION, it is one cheap image answering one question: is this the place I meant? Approving it early is
 * what makes every shot built on it predictable, and the same reasoning already governs a character's locked
 * face.
 *
 * PROMPT PLUS PICTURE, never one or the other (owner law 2026-08-29). The description writes the plate; an
 * upload replaces it outright for someone who already has the image. Both doors, on every plate.
 *
 * THE WORDING IS NOT WRITTEN HERE ANY MORE (F2, 2026-09-12). This component used to prepend its own lead
 * string — "Empty establishing photograph of a place, no people" for one caller, "Product photograph of a
 * single object" for another — which meant the house rules for a reference image lived in three files, none
 * of which knew the show's look. The server composes the prompt now from the project's own row plus its look,
 * so a set, a prop and a face made an hour apart read as one production. This surface says WHICH thing to
 * picture and shows the result.
 *
 * REGENERATING IS NORMAL, not a correction. The control stays visible on a plate that already exists, because
 * the second attempt is usually the good one and hiding it behind a delete would make people keep a picture
 * they only half-liked.
 */

export interface ReferencePlateProps {
  /** The Storage path of the plate, if one exists yet. */
  path?: string | null;
  onChange: (path: string | null) => void;
  /** The project this belongs to, and the row inside it — the server reads the subject from there. */
  seriesId?: string | null;
  kind: PlateKind;
  itemId: string;
  /** Whether the row has been described yet. Nothing to picture is a disabled button with a reason. */
  described: boolean;
  /**
   * Write the project before asking for the plate. The server composes from the saved document, so without
   * this a plate requested seconds after typing is a plate for a row the server has never seen.
   */
  onFlush?: () => Promise<void>;
  /** Where an upload belongs in Storage. */
  folder: string;
  /** What is being pictured ("The flooded market"), for labels a screen reader can make sense of. */
  name?: string;
  onNeedsTopUp?: () => void;
  disabled?: boolean;
}

const ReferencePlate: React.FC<ReferencePlateProps> = ({
  path, onChange, seriesId, kind, itemId, described, folder, name, onFlush, onNeedsTopUp, disabled,
}) => {
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);
  /** Looking at it properly (owner 2026-09-13) — 80 pixels cannot answer "is this the place I meant?". */
  const [enlarged, setEnlarged] = useState(false);
  /** True when the last generation returned a local PLACEHOLDER rather than a model's work (G0). */
  const [stubbed, setStubbed] = useState(false);
  const url = useStorageUrl(path || null);
  const { showError } = useOptionalToast();
  const what = name || 'this';

  const generate = useCallback(async () => {
    if (!described || busy || !seriesId) return;
    setBusy(true);
    setBroken(false);
    setStubbed(false);
    try {
      // The row has to EXIST server-side before it can be pictured — see `onFlush`. A failed save stops here
      // rather than generating a plate against facts the server does not have.
      if (onFlush) {
        try {
          await onFlush();
        } catch {
          showError('Your series has not saved yet', 'That change did not reach us. Try again in a moment.');
          return;
        }
      }
      // The server draws it in ISOLATION under the show's look: no character, no camera move, no story. A
      // plate is only useful if it shows the thing and nothing else — anything extra is noise the shots inherit.
      const plate = await generateReferencePlate(seriesId, kind, itemId);
      if (plate.needsTopUp) { onNeedsTopUp?.(); return; }
      setStubbed(!!plate.stub);
      if (!plate.path) {
        showError('That did not come back', 'The reference was not generated. Try again in a moment.');
        return;
      }
      onChange(plate.path);
    } catch {
      showError('Could not make that reference', 'Nothing was generated. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }, [described, busy, seriesId, kind, itemId, onFlush, onChange, onNeedsTopUp, showError]);

  return (
    <div className="mt-2 flex items-start gap-2">
      {path && (
        <ImageLightbox
          open={enlarged}
          onOpenChange={setEnlarged}
          images={[{ path, caption: name }]}
          title={`Reference for ${what}`}
        />
      )}
      {/* THE PICTURE ITSELF, at a size you can actually judge. A thumbnail too small to assess would defeat
          the point of making it early. */}
      {path && (
        <div className="relative shrink-0">
          {url && !broken ? (
            // A BUTTON, not an image with a click handler: enlarging is an action, so it has to be reachable
            // from the keyboard and announce itself as one.
            <button
              type="button"
              onClick={() => setEnlarged(true)}
              aria-label={`Enlarge the reference for ${what}`}
              data-testid="plate-enlarge"
              className="group relative block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400"
            >
              <img
                src={url}
                alt={`Reference for ${what}`}
                data-testid="plate-image"
                onError={() => setBroken(true)}
                className="h-20 w-20 rounded-lg border border-secondary-200 object-cover transition-opacity
                           group-hover:opacity-80 dark:border-secondary-700"
              />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                <Maximize2 className="h-4 w-4 text-white drop-shadow" aria-hidden />
              </span>
            </button>
          ) : (
            <span
              data-testid="plate-pending"
              className="flex h-20 w-20 items-center justify-center rounded-lg border border-secondary-200 dark:border-secondary-700"
            >
              {broken
                ? <ImageOff className="h-4 w-4 text-secondary-400" aria-hidden />
                : <Loader2 className="h-4 w-4 animate-spin text-secondary-400" aria-hidden />}
            </span>
          )}
          <Tooltip content="Remove this reference">
            <button
              type="button"
              onClick={() => onChange(null)}
              aria-label={`Remove the reference for ${what}`}
              data-testid="plate-remove"
              className="absolute -right-1.5 -top-1.5 rounded-full border border-secondary-200 bg-white p-0.5
                         text-secondary-500 transition-colors hover:text-secondary-900 dark:border-secondary-600
                         dark:bg-secondary-900 dark:hover:text-white"
            >
              <X className="h-3 w-3" />
            </button>
          </Tooltip>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <div className="flex flex-wrap items-center gap-1">
          <Tooltip content={described
            ? 'Draw it from the description, on its own, so you can check it before any shot uses it'
            : 'Describe it first — there is nothing to draw from yet'}
          >
            <button
              type="button"
              onClick={() => void generate()}
              disabled={disabled || busy || !described || !seriesId}
              data-testid="plate-generate"
              className={`inline-flex items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                         ${TYPE.meta} text-secondary-600 transition-colors hover:border-secondary-400
                         disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-300`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" />
                : path ? <RefreshCw className="h-3 w-3" />
                  : <Sparkles className="h-3 w-3" />}
              {busy ? 'Drawing…' : path ? 'Try again' : 'Show me this'}
            </button>
          </Tooltip>

          {/* The other door. Someone who HAS the picture should never have to describe their way back to it. */}
          <ReferenceUpload
            path={null}
            onChange={(p) => onChange(p)}
            folder={folder}
            label={path ? 'Use my own' : 'Upload one'}
            disabled={disabled || busy}
          />
        </div>
        {/* SAY WHEN IT IS A PLACEHOLDER (G0). Locally, with no media provider configured, generation
            succeeds and returns a grey stand-in — indistinguishable from a real frame unless it is labelled,
            and the reason "I cannot see our generated images" read as a rendering fault. */}
        {stubbed ? (
          <span className={`${TYPE.micro} text-secondary-500 dark:text-secondary-400`} data-testid="plate-stub">
            Placeholder — no image model is configured here, so nothing was generated and nothing was charged.
          </span>
        ) : (
          <span className={`${TYPE.micro} text-secondary-400 dark:text-secondary-500`}>
            {path
              ? 'Every shot here starts from this picture.'
              : 'Optional — but a picture holds a place together far better than words do.'}
          </span>
        )}
      </div>
    </div>
  );
};

export default ReferencePlate;
